import React from "react";
import { MapPin } from "lucide-react";
import GeotabBadge from "./GeotabBadge.jsx";
import { HoverRows } from "./GeotabHover.jsx";
import GeotabOffPlanDetails from "./GeotabOffPlanDetails.jsx";
import GeotabDriverSpeed from "./GeotabDriverPerformance.jsx";
import { compareToFleet, driverRows, fleetRow, GAP_MINUTES, gapCounts, hoursTone, speedCounts, sortRows } from "./geotabDashboard.js";
import { fmtDelta, fmtDuration } from "./geotabFormat.js";

// Tableau « Par chauffeur » : chaque ligne s'ouvre sur la fiche du chauffeur (hors plan, rapidité vs collègues).
// Pas de flèche ▲▼ : partout ailleurs elle veut dire une tendance. Le signe donne le sens de l'écart ; la couleur dit
// moins bien (rouge), mieux (vert) ou plus court que prévu (violet).
const ONE = new Intl.NumberFormat("fr-CA", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const INT = new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 0 });
const litresPerDay = (litres, days) => `${ONE.format(litres / days)} L par jour`;
const CELL = "border-t border-slate-100 px-1.5 py-[3px] text-[12.5px] whitespace-nowrap tabular-nums";
const HEAD = "px-1.5 pb-1 pt-0 text-left text-[10px] font-bold uppercase tracking-wide text-slate-600 whitespace-nowrap";
const TONES = { bad: "font-bold text-[#c5524a]", good: "font-bold text-accent", short: "font-bold text-[#8a63c4]" };
const DOTTED = "cursor-help border-b border-dotted border-slate-400";

const COLUMNS = [
  ["name", "Chauffeur"], ["days", "Jours"], ["loads", "Chargements"], ["deliveries", "Livraisons"], ["order", "Ordre"], ["pm", "Chargés PM"],
  // « Précharg. » : les minutes sont plus larges que les %, et ce titre fixait la largeur de sa colonne.
  ["preload", "Précharg.", "Préchargements"], ["p1", "P1 à l'heure"],
  ["loadingGap", "Chargement", "Sur place · chargement : écart au prévu en minutes par visite"],
  ["deliveryGap", "Livraison", "Sur place · livraison : écart au prévu en minutes par visite"],
  ["off", "par jour"], ["hoursPlanned", "Prévues"], ["hoursReal", "Réelles"], ["hoursGap", "Écart moyen"], ["hoursOver", "À +30 min"],
  ["km", "Réels vs prévus"], ["speeding", "Excès / 1 000 km", "Conduite : excès de vitesse par 1 000 km roulés"], ["idle", "Ralenti / jour"],
  ["l100", "L/100 km", "Carburant : litres par 100 km en roulant"],
    ["speedRoute", "Route", "Rapidité vs collègues : route"], ["speedLoading", "Charg.", "Rapidité vs collègues : chargement"], ["speedDelivery", "Livr.", "Rapidité vs collègues : livraison"],
];
const SEP = new Set(["loadingGap", "off", "hoursPlanned", "km", "speeding", "speedRoute"]);
// Arrondi avant le signe : −0,3 % s'affiche « 0 % ».
const pctText = (value) => { const r = Math.round(value); return `${r > 0 ? "+" : r < 0 ? "−" : ""}${Math.abs(r)} %`; };

function Tone({ tone, children }) {
  return tone ? <span className={TONES[tone]}>{children}</span> : children;
}

export default function GeotabDriversTable({ data, onOpenDriver }) {
  const [sort, setSort] = React.useState({ key: "days", dir: "desc" });
  const [open, setOpen] = React.useState(null);
  const sheetId = React.useId();
  React.useEffect(() => setOpen(null), [data]);
  const fleet = fleetRow(data);
  const rows = driverRows(data);
  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const openSheet = (name, tab) => setOpen((o) => (o?.name === name && (!tab || o.tab === tab) ? null : { name, tab: tab || o?.tab || "off" }));
  const colored = (row) => row.regular && row !== fleet;
  // Comparé à toute la flotte : couleur seulement au-delà des seuils de la flotte.
  const vsFleet = (row, key, text) => {
    if (row[key] == null) return <span className="text-slate-300">—</span>;
    if (!colored(row) || fleet[key] == null) return text;
    const result = compareToFleet(key, row[key], fleet[key]);
    return <Tone tone={result === "worse" ? "bad" : result === "better" ? "good" : null}>{text}</Tone>;
  };
  const frac = (row, [a, b]) => (b ? <span className={colored(row) && a < b ? TONES.bad : ""}>{`${a} / ${b}`}</span> : <span className="text-slate-300">—</span>);
  const placeGap = (row, kind) => {
    const d = row[kind];
    if (d?.gap == null) return <span className="text-slate-300">—</span>;
    const text = colored(row) ? <Tone tone={gapCounts(d.gap) ? (d.gap > 0 ? "bad" : "short") : null}>{fmtDelta(d.gap)}</Tone> : fmtDelta(d.gap);
    return (
      <HoverRows title={kind === "loading" ? "Sur place · chargement" : "Sur place · livraison"}
        rows={[["Prévu", fmtDuration(d.planned)], ["Réel", fmtDuration(d.real)], ["Visites", String(d.count)]]}>
        <span className={DOTTED}>{text}</span>
      </HoverRows>
    );
  };
  const speedCell = (row, metric) => {
    if (row === fleet) return <span className="text-slate-300">réf.</span>;
    const s = row.speed?.[metric];
    if (!s) return <span className="text-slate-300">—</span>;
    // Route en % (trajets d'environ une heure) ; chargement et livraison en minutes par visite.
    const route = metric === "route";
    const value = route ? s.pct : s.minutes;
    const shown = route ? pctText(s.pct) : fmtDelta(s.minutes);
    const text = colored(row) ? <Tone tone={speedCounts(metric, s) ? (value > 0 ? "bad" : "good") : null}>{shown}</Tone> : shown;
    const unit = route ? "trajets" : "visites";
    return (
      <HoverRows title="Rapidité vs collègues" rows={[
        !route && ["Mesure", "par visite, vs collègues aux mêmes sites"],
        route && ["Mesure", "temps réel / prévu vs collègues"],
        ["Base", `${s.days} jours · ${s.count} ${unit}`],
      ]}><span className={DOTTED}>{text}</span></HoverRows>
    );
  };
  // Excès, ralenti et carburant : le détail en km, minutes et litres dans la bulle.
  const speedingCell = (row) => {
    const d = row.driving;
    if (row.speeding == null) return <span className="text-slate-300">—</span>;
    return (
      <HoverRows title="Excès de vitesse" rows={[
        ["Excès", `${d.speeding} sur ${INT.format(d.km)} km`], row.speedingPerDay != null && ["Par jour", ONE.format(row.speedingPerDay)],
        ["Distance en excès", `${ONE.format((100 * d.speeding_km) / d.km)} %`], ["Durée totale", fmtDuration(d.speeding_minutes)],
      ]}><span className={DOTTED}>{vsFleet(row, "speeding", ONE.format(row.speeding))}</span></HoverRows>
    );
  };
  // Geotab compte aussi la soufflerie et la vis comme du ralenti : les litres aux sites prévus sont à part.
  const idleCell = (row) => {
    const text = vsFleet(row, "idle", row.idle == null ? "" : fmtDuration(row.idle));
    const f = row.driving?.fuel;
    if (row.idle == null || !f?.days) return text;
    return (
      <HoverRows title="Ralenti" rows={[
        ["Hors sites prévus", `${fmtDuration(row.idle)} et ${litresPerDay(f.idle_off, f.days)}`],
        ["Aux sites prévus", `${litresPerDay(f.idle_site, f.days)} (soufflerie, vis)`],
      ]}><span className={DOTTED}>{text}</span></HoverRows>
    );
  };
  const fuelCell = (row) => {
    const f = row.driving?.fuel;
    if (row.l100 == null) {
      return row.driving?.km ? (
        <HoverRows title="Carburant" rows={[["Geotab", "carburant incomplet pour ses camions"]]}><span className={`${DOTTED} text-slate-300`}>—</span></HoverRows>
      ) : <span className="text-slate-300">—</span>;
    }
    return (
      <HoverRows title="Carburant" rows={[
        ["En roulant", `${ONE.format(row.l100)} L/100 km`], ["Avec les arrêts", `${ONE.format((100 * f.litres) / f.km)} L/100 km`],
        row.trucks.length > 0 && ["Camions", row.trucks.join(", ")],
        row !== fleet && fleet.l100 != null && ["Flotte en roulant", `${ONE.format(fleet.l100)} L/100 km`],
      ]}><span className={DOTTED}>{vsFleet(row, "l100", ONE.format(row.l100))}</span></HoverRows>
    );
  };
  const hoursCell = (row, h) => {
    const chip = <GeotabBadge tone={colored(row) ? hoursTone(h.gap) : "ok"}>{fmtDelta(h.gap)}</GeotabBadge>;
    return (
      <HoverRows title="Heures travaillées · moyenne par jour" rows={[["Prévues", fmtDuration(h.planned)], ["Réelles", fmtDuration(h.real)],
        ["Écart", h.planned ? `${pctText((100 * h.gap) / h.planned)} du prévu` : "—"]]}>
        <span className="inline-block min-w-[66px] cursor-help align-middle">{chip}</span>
      </HoverRows>
    );
  };

  const line = (row) => {
    const h = row.hours;
    const fleetLine = row === fleet;
    const expanded = open?.name === row.name;
    // Toute la flotte s'ouvre aussi, sur ses lieux hors plan seulement.
    const details = fleetLine ? { off_stops: data.fleet.off_stops, bar: { truck_days: data.bar.truck_days } } : data.drivers.find((driver) => driver.name === row.name);
    const td = (extra = "") => `${CELL} text-right ${fleetLine ? "border-b-2 border-b-slate-400" : ""} ${expanded ? "bg-slate-100" : ""} ${extra}`;
    return (
      <React.Fragment key={fleetLine ? "fleet" : row.name}>
        <tr className={`cursor-pointer hover:[&>td]:bg-slate-50 ${fleetLine ? "font-bold" : row.regular ? "" : "text-slate-500"}`}
          onClick={() => openSheet(row.name, fleetLine ? "off" : undefined)}>
          <td className={`${td("max-w-[220px] truncate !text-left")} ${expanded ? "shadow-[inset_3px_0_0_var(--color-accent)]" : ""}`}>
            <button type="button" aria-expanded={expanded} aria-controls={expanded ? sheetId : undefined}
              onClick={(event) => { event.stopPropagation(); openSheet(row.name, fleetLine ? "off" : undefined); }}
              className={`border-0 bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 ${fleetLine ? "font-bold text-slate-900" : `font-semibold ${row.regular ? "text-accent" : "text-slate-500"}`}`}>
              <span aria-hidden="true" className={`mr-1 inline-block w-2.5 text-[10px] text-slate-500 transition-transform ${expanded ? "rotate-90 !text-accent" : ""}`}>▸</span>{row.name}
            </button>
          </td>
          <td className={td()}>{row.days}</td>
          <td className={td()}>{row.loads == null ? "—" : vsFleet(row, "loads", `${row.loadCounts[0]} / ${row.loadCounts[1]} · ${row.loads} %`)}</td>
          <td className={td()}>{vsFleet(row, "deliveries", `${row.deliveries} %`)}</td>
          <td className={td()}>{vsFleet(row, "order", `${row.order} %`)}</td>
          {["pm", "preload", "p1"].map((key) => <td key={key} className={td()}>{frac(row, row[key])}</td>)}
          <td className={td("border-l border-l-slate-200")}>{placeGap(row, "loading")}</td>
          <td className={td()}>{placeGap(row, "delivery")}</td>
          <td className={td("border-l border-l-slate-200")}>
            {row.off == null ? "—" : (
              <button type="button" aria-label={`Hors plan de ${row.name} : ${fmtDuration(row.off)} par jour`}
                onClick={(event) => { event.stopPropagation(); openSheet(row.name, "off"); }}
                className="inline-flex items-center gap-1 border-0 border-b border-dashed border-current bg-transparent p-0 text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40">
                <MapPin size={11} strokeWidth={2.4} aria-hidden="true" />{vsFleet(row, "off", fmtDuration(row.off))}
              </button>
            )}
          </td>
          <td className={td("border-l border-l-slate-200")}>{h ? fmtDuration(h.planned) : "—"}</td>
          <td className={td("font-semibold")}>{h ? fmtDuration(h.real) : "—"}</td>
          <td className={td()}>{h ? hoursCell(row, h) : "—"}</td>
          <td className={td()}>{h ? `${h.over} / ${h.days}` : "—"}</td>
          <td className={td("border-l border-l-slate-200")}>{vsFleet(row, "km", row.km == null ? "" : `${row.km >= 0 ? "+" : "−"}${ONE.format(Math.abs(row.km))} %`)}</td>
          <td className={td("border-l border-l-slate-200")}>{speedingCell(row)}</td>
          <td className={td()}>{idleCell(row)}</td>
          <td className={td()}>{fuelCell(row)}</td>
          <td className={td("border-l border-l-slate-200")}>{speedCell(row, "route")}</td>
          <td className={td()}>{speedCell(row, "loading")}</td>
          <td className={td()}>{speedCell(row, "delivery")}</td>
        </tr>
        {expanded && (
          <tr><td colSpan={COLUMNS.length} className="border-t border-slate-200 bg-slate-50 px-1.5 py-2">
            <DriverSheet id={sheetId} row={row} driver={details} data={data} tab={fleetLine ? "off" : open.tab} fleetLine={fleetLine}
              onTab={(tab) => setOpen({ name: row.name, tab })} onClose={() => setOpen(null)} onOpenDriver={onOpenDriver} />
          </td></tr>
        )}
      </React.Fragment>
    );
  };
  const regular = sortRows(rows.filter((r) => r.regular), sort.key, sort.dir);
  const occasional = sortRows(rows.filter((r) => !r.regular), sort.key, sort.dir);
  return (
    <section className="flex min-w-0 flex-col overflow-hidden rounded-[4px] border border-slate-200 bg-white px-3.5 py-2.5">
      <header className="mb-1.5">
        <h2 className="m-0 text-[15px] font-bold text-slate-900">Par chauffeur</h2>
        <p className="m-0 text-[11px] text-slate-600">Chargements : réalisés / prévus · taux. Sur place : écart moyen au prévu, en minutes par visite. Cliquez une ligne pour ouvrir la fiche du chauffeur ; un titre trie la colonne ; survolez un chiffre souligné pour le détail.</p>
        <p className="m-0 mt-1 flex flex-wrap gap-x-3.5 gap-y-0.5 text-[11px] text-slate-700">
          <span>Le signe <b>+</b> ou <b>−</b> donne le sens de l'écart ; la couleur dit si c'est moins bien ou mieux.</span>
          <span><b className={TONES.bad}>rouge</b> moins bien</span><span><b className={TONES.good}>vert</b> mieux</span>
          <span><b className={TONES.short}>violet</b> plus court que prévu</span><span className="text-slate-400"><b>gris</b> dans la normale</span>
          <span className="text-slate-500">Taux, hors plan, km, conduite, carburant : comparés à la <b>moyenne de toute la flotte</b> (ligne du haut). Sur place : au <b>prévu</b>, couleur à ±{GAP_MINUTES} min par visite. Heures : au <b>prévu</b>. Rapidité : à la <b>médiane des collègues</b> aux mêmes endroits ; chargement et livraison en minutes par visite, couleur à ±{GAP_MINUTES} min. L/100 km : en roulant, sans le carburant brûlé à l'arrêt.</span>
        </p>
      </header>
      <div className="min-w-0 overflow-x-auto">
        <table className="w-full min-w-0 border-collapse">
          <thead>
            <tr className="text-center text-[10px] font-bold uppercase tracking-[0.06em] text-slate-900">
              <th colSpan={2} /><th colSpan={6}>Respect du plan</th>
              <th colSpan={2} className="border-l border-l-slate-200">Sur place · écart au prévu</th>
              <th className="border-l border-l-slate-200">Hors plan</th>
              <th colSpan={4} className="border-l border-l-slate-200">Heures travaillées · journées complètes</th><th className="border-l border-l-slate-200">Km</th>
              {/* Un seul titre sur trois colonnes : « Carburant » au-dessus de L/100 km seul élargissait le tableau de 30 px. */}
              <th colSpan={3} className="border-l border-l-slate-200">Conduite et carburant</th>
              <th colSpan={3} className="border-l border-l-slate-200">Rapidité vs collègues</th>
            </tr>
            <tr>
              {COLUMNS.map(([key, label, description]) => (
                <th key={key} className={`${HEAD} ${key === "name" ? "" : "text-right"} ${SEP.has(key) ? "border-l border-l-slate-200" : ""}`}
                  aria-sort={sort.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
                  <button type="button" aria-label={description} className="rounded-[4px] border-0 bg-transparent p-0 font-bold uppercase text-inherit hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40" onClick={() => toggleSort(key)}>
                    {label}{sort.key === key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {line(fleet)}
            {regular.map(line)}
            {occasional.length > 0 && (
              <tr><td colSpan={COLUMNS.length} className="border-t-2 border-slate-200 px-1.5 pb-0.5 pt-2 text-[10.5px] font-bold uppercase tracking-wide text-slate-600">
                Moins de 10 jours : à lire avec prudence (sans couleur)</td></tr>
            )}
            {occasional.map(line)}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// Fiche du chauffeur, sous sa ligne : deux onglets, gardée à gauche quand le tableau défile.
function DriverSheet({ id, row, driver, data, tab, fleetLine, onTab, onClose, onOpenDriver }) {
  const offTotal = (driver?.off_stops || []).reduce((sum, stop) => sum + stop.minutes, 0);
  const TAB = "border-0 border-b-2 bg-transparent px-2.5 pb-1.5 pt-2 text-[12px] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40";
  const BUTTON = "h-6 rounded-[4px] border border-slate-300 bg-white px-2 text-[11.5px] font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50";
  return (
    <section id={id} aria-label={`Fiche de ${row.name}`} className="sticky left-2 w-full max-w-[min(1180px,calc(100cqw-3rem))] rounded-[4px] border border-slate-200 bg-white text-slate-700">
      <div role="tablist" aria-label={`Fiche de ${row.name}`} className="flex flex-wrap items-end gap-1 border-b border-slate-200 px-2.5">
        {[["off", "Hors plan", fmtDuration(offTotal)], !fleetLine && ["speed", "Rapidité vs collègues", null]].filter(Boolean).map(([key, label, note]) => (
          <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => onTab(key)}
            className={`${TAB} ${tab === key ? "border-accent text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {label}{note && <span className="ml-1 font-semibold text-slate-500">{note}</span>}
          </button>
        ))}
        <span className="ml-auto flex gap-1.5 self-center py-1">
          {!fleetLine && <button type="button" className={BUTTON} onClick={() => onOpenDriver(row.name)}>Voir au Calibrage</button>}
          <button type="button" className={BUTTON} onClick={onClose}>Fermer</button>
        </span>
      </div>
      <div role="tabpanel" className="px-3 py-2">
        {tab === "off"
          ? <GeotabOffPlanDetails name={row.name} stops={driver?.off_stops} days={driver?.bar.truck_days} />
          : <GeotabDriverSpeed driver={driver} row={row} data={data} />}
      </div>
    </section>
  );
}
